import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Bus, ArrowRight } from "lucide-react";
import { formatErr } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setBrand(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const err = params.get("err");
    if (err) toast.error(err);
  }, [params]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, pw);
      toast.success(`Welcome, ${u.name}`);
      if (u.role === "driver") nav("/driver-portal");
      else if (u.role === "client") nav("/client-portal");
      else if (u.role === "sales") nav("/sales-portal");
      else nav("/dashboard");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  }

  function googleLogin() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }

  function fill(e, p) { setEmail(e); setPw(p); }

  return (
    <div className="min-h-screen grid md:grid-cols-2 grain">
      <div className="hidden md:flex relative overflow-hidden">
        <img
          src={brand.login_image_url || "https://images.unsplash.com/photo-1492168732976-2676c584c675?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"}
          alt="fleet"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[hsl(224_60%_6%)] via-[hsl(224_60%_6%_/_0.6)] to-transparent" />
        <div className="relative z-10 p-10 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt="logo" className="w-10 h-10 rounded-xl object-cover"/>
            ) : (
              <div className="w-10 h-10 rounded-xl bg-amber-500 grid place-items-center text-[hsl(224_60%_6%)] font-black font-display text-lg">{(brand.brand_name || "A")[0]}</div>
            )}
            <div>
              <div className="font-display font-bold text-lg">{brand.brand_name || "Ashok Netravels"}</div>
              <div className="text-[11px] tracking-[0.25em] uppercase text-amber-300/80">{brand.tagline || "Vromon Anuragee"}</div>
            </div>
          </div>
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-amber-300/80 mb-3">Fleet Command Center</div>
            <h1 className="font-display text-4xl lg:text-5xl font-bold leading-tight max-w-[500px]">
              Every kilometre. Every rupee. Every renewal — <span className="text-amber-400">accounted for</span>.
            </h1>
            <p className="mt-4 text-slate-300 max-w-[460px] text-sm">
              Manage vehicles, drivers, vendors, trips & accounting from one command center. Never miss another Tax, Insurance, Fitness or Permit expiry.
            </p>
          </div>
          <div className="text-xs text-slate-400">© {new Date().getFullYear()} Ashok Netravels · Kolkata</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-14">
        <Card className="w-full max-w-md bg-[hsl(224_45%_9%)] border-border p-8 relative z-10">
          <div className="flex items-center gap-2 mb-6">
            <Bus className="w-5 h-5 text-amber-400" />
            <div className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Sign in</div>
          </div>
          <h2 className="font-display text-3xl font-bold mb-1">Welcome back</h2>
          <p className="text-sm text-muted-foreground mb-6">Continue managing your fleet operations.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="login-email-input"
                className="mt-1.5 bg-[hsl(224_40%_12%)] border-border h-11"
              />
            </div>
            <div>
              <Label htmlFor="pw" className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
              <Input
                id="pw" type="password" required value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="mt-1.5 bg-[hsl(224_40%_12%)] border-border h-11"
              />
            </div>
            <Button
              type="submit" disabled={busy}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold amber-glow"
            >
              {busy ? "Signing in…" : (<span className="inline-flex items-center gap-2">Sign in <ArrowRight className="w-4 h-4" /></span>)}
            </Button>
            <div className="flex items-center gap-3 my-1">
              <div className="flex-1 h-px bg-border"></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">or</div>
              <div className="flex-1 h-px bg-border"></div>
            </div>
            <button
              type="button"
              onClick={googleLogin}
              data-testid="google-login-btn"
              className="w-full h-11 rounded-md border border-border bg-white text-slate-900 hover:bg-slate-100 font-semibold inline-flex items-center justify-center gap-3 transition-colors">
              <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.56c2.08-1.92 3.28-4.75 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.56-2.77c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.28-1.93-6.14-4.52H2.18v2.84A11 11 0 0 0 12 23z"/>
                <path fill="#FBBC05" d="M5.86 14.12A6.6 6.6 0 0 1 5.5 12c0-.74.13-1.46.36-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.96l3.68-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.04l3.68 2.84C6.72 7.3 9.14 5.38 12 5.38z"/>
              </svg>
              Continue with Google
            </button>
            <div className="text-[11px] text-muted-foreground text-center -mt-2">
              Clients: only trip-confirmed accounts (with advance received) can sign in with Google.
            </div>
            <div className="flex justify-end -mt-1">
              <Link to="/forgot-password" data-testid="forgot-link-inline" className="text-xs text-amber-300 hover:underline">Forgot password?</Link>
            </div>
          </form>

          <div className="mt-5 text-xs text-center text-muted-foreground">
            New client? <Link to="/register" className="text-amber-400 hover:underline">Create an account</Link>
            <span className="mx-2">·</span>
            <Link to="/forgot-password" className="text-amber-400 hover:underline" data-testid="forgot-link">Forgot password?</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
